import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import { Box } from "@mantine/core";
import { ColDef } from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  baseTheme,
  paginationPageSize,
  paginationPageSizeSelector,
  repositoryPos,
} from "../../../../../_base/_const/_constVar";
import useDynamicHeight from "../../../../../_base/component/Core/hookBase";
import { SelectListItemBase } from "../../../../../_base/model/_base/SelectListItemBase";
import { MessageResponse } from "../../../../../model/MessageResponse";
import { PaymentReceiptDetailModel } from "../../../../../model/PaymentReceiptDetailModel";

// Interface cho danh sách đầu tiên
interface DocumentInfo {
  id: number;
  code: string;
  originalDocuments: string;
  originalDocumentNumber: string;
  codeForFees: string;
  occurringCredit: string;
  debtObjectCode: string;
  debtObjectName: string;
  employeeFund: string;
  branchesFund: string;
  description: string;
  departmentFund: string;
  nameattr: string;
  codeattr: string;
  fee: string;
  contract: string;
  stt: number;
  orderNumber: string;
  attribute2: string;
  attribute7: string;
  amountTotal: number;
  amountPaid: number;
  amountRemaining: number;
  allocatedAmount: number;
  note: string;
  codeBranch: string;
}

interface OrderDetailProps {
  document: PaymentReceiptDetailModel;
}

export const OrderDetail: React.FC<OrderDetailProps> = ({ document }) => {
  const jsonObject = JSON.parse(document.attribute5 || "{}");
  const [dataBranchs, setDataBranchs] = useState<SelectListItemBase[]>([]);
  const [dataWarehouse, setDataWarehouse] = useState<SelectListItemBase[]>([]);
  const gridRef = useRef<AgGridReact<DocumentInfo>>(null);

  const height = useDynamicHeight(280);
  const containerStyle = useMemo(
    () => ({ width: "100%", height: height, marginTop: "15px" }),
    [height]
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      editable: false,
      filter: true,
      floatingFilter: true,
    };
  }, []);

  function currencyFormatter(params: any) {
    return formatNumber(params);
  }
  function formatNumber(number: any) {
    return Math.floor(number).toLocaleString();
  }
  const column1 = useMemo<ColDef<DocumentInfo>[]>(() => {
    const baseColumns: ColDef<DocumentInfo>[] = [
      {
        field: "stt",
        headerName: "#",
        editable: false,
        lockPinned: true,
        minWidth: 100,
        maxWidth: 100,
        pinned: "left",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{(params?.node?.rowIndex ?? 0) + 1}</>;
        },
      },
      {
        field: "orderNumber",
        headerName: "Số đơn hàng",
        editable: false,
        pinned: "left",
        filter: "agTextColumnFilter",
      },
      {
        field: "attribute2",
        headerName: "Mã đối tượng công nợ",
        editable: false,
        lockPinned: true,
        filter: "agTextColumnFilter",
      },
      {
        field: "attribute7",
        headerName: "Tên đối tượng công nợ",
        editable: false,
        filter: "agTextColumnFilter",
      },
      {
        field: "amountTotal",
        headerName: "Tổng tiền hóa đơn",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{currencyFormatter(params.data?.amountTotal ?? 0)}</>;
        },
      },
      {
        field: "amountPaid",
        headerName: "Số tiền đã thanh toán",
        editable: false,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{currencyFormatter(params.data?.amountPaid ?? 0)}</>;
        },
      },
      {
        field: "amountRemaining",
        headerName: "Số còn phải thanh toán",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{currencyFormatter(params.data?.amountRemaining ?? 0)}</>;
        },
      },
      {
        field: "allocatedAmount",
        headerName: "Số tiền phân bổ",
        editable: false,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{currencyFormatter(params.data?.allocatedAmount ?? 0)}</>;
        },
      },
      {
        field: "note",
        headerName: "Diễn giải",
        editable: false,
        minWidth: 375,
        lockPinned: true,
        filter: "agTextColumnFilter",
      },
    ];

    const additionalColumns: ColDef<DocumentInfo>[] = [
      {
        field: "codeattr",
        headerName: "TK phải thu",
        editable: false,
        filter: "agTextColumnFilter",
      },
      {
        field: "nameattr",
        headerName: "Tên TK phải thu",
        editable: false,
        filter: "agTextColumnFilter",
      },
    ];

    const hasCodeAttr = jsonObject?.some((item: any) => item.codeattr);

    if (!hasCodeAttr) return baseColumns;

    const insertIndex =
      baseColumns.findIndex((col) => col.field === "allocatedAmount") + 1;

    const newColumns = [
      ...baseColumns.slice(0, insertIndex),
      ...additionalColumns,
      ...baseColumns.slice(insertIndex),
    ];

    return newColumns;
  }, [jsonObject]);

  const column2 = useMemo<ColDef<DocumentInfo>[]>(
    () => [
      {
        field: "stt",
        headerName: "#",
        editable: false,
        lockPinned: true,
        minWidth: 100,
        maxWidth: 100,
        pinned: "left",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{(params?.node?.rowIndex ?? 0) + 1}</>;
        },
      },
      {
        field: "originalDocuments",
        headerName: "Tên chứng từ gốc",
        editable: false,
        minWidth: 170,
        maxWidth: 170,
        pinned: "left",
        filter: "agTextColumnFilter",
      },
      {
        field: "originalDocumentNumber",
        headerName: "Số chứng từ gốc",
        editable: false,
        lockPinned: true,
        minWidth: 170,
        maxWidth: 170,
        filter: "agTextColumnFilter",
      },
      {
        field: "code",
        headerName: "Mã phí",
        editable: false,
        filter: "agTextColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{params.data?.code + " - " + params.data?.codeForFees}</>;
        },
      },
      {
        field: "occurringCredit",
        headerName:
          document.typeReceipt === 1 ? "Phát sinh có" : "Phát sinh nợ",
        editable: false,
        lockPinned: true,
        minWidth: 170,
        maxWidth: 170,
        filter: "agTextColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return <>{currencyFormatter(params.data?.occurringCredit ?? 0)}</>;
        },
      },
      {
        field: "debtObjectCode",
        headerName: "Mã đối tượng công nợ",
        editable: false,
        filter: "agTextColumnFilter",
      },
      {
        field: "debtObjectName",
        headerName: "Tên đối tượng công nợ",
        editable: false,
        lockPinned: true,
        filter: "agTextColumnFilter",
      },
      {
        field: "description",
        headerName: "Nội dung",
        editable: false,
        filter: "agTextColumnFilter",
      },
      {
        field: "branchesFund",
        headerName: "Chi nhánh",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          return (
            <>
              {params.data?.branchesFund +
                " - " +
                (dataBranchs.find(
                  (ele) =>
                    String(ele.att2) === String(params.data?.branchesFund)
                )?.text ?? "")}
            </>
          );
        },
      },
      {
        field: "employeeFund",
        headerName: "Mã nhân viên",
        editable: false,
        lockPinned: true,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: CustomCellRendererProps<DocumentInfo>) => {
          const x = dataWarehouse.find(
            (ele) => String(ele.att1) === String(params.data?.employeeFund)
          );
          return (
            <>
              {" "}
              {params.data?.employeeFund
                ? `${params.data?.employeeFund + " - " + x?.text}`
                : null}
            </>
          );
        },
      },
      {
        field: "fee",
        headerName: "CTKM",
        editable: false,
        lockPinned: true,
        minWidth: 130,
        filter: "agTextColumnFilter",
      },
      {
        field: "contract",
        headerName: "Hợp đồng",
        editable: false,
        lockPinned: true,
        filter: "agTextColumnFilter",
      },
    ],
    [dataWarehouse, dataBranchs]
  );

  const fetchDataBranchs = async () => {
    const response = await repositoryPos.get<
      MessageResponse<SelectListItemBase[]>
    >("/api/v1/TblDmInventory/get-select-branch?auth=false");

    if (response && response.success) {
      let result = response.data;
      setDataBranchs(result);
    }
  };

  const fetchDataWarehouse = async () => {
    const dataApi = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblDmEmployee/get-select"
    );

    if (dataApi && dataApi.success) {
      setDataWarehouse(dataApi.data);
    }
  };

  useEffect(() => {
    fetchDataWarehouse();
    fetchDataBranchs();
  }, []);

  return (
    <Box mt={15}>
      <div style={containerStyle}>
        <div style={gridStyle} className={"ag-theme-quartz"}>
          <AgGridReact<DocumentInfo>
            ref={gridRef}
            rowData={jsonObject}
            theme={baseTheme}
            columnDefs={
              (document.typeReceipt === 1 && document.type === 1) ||
              document.type === 6
                ? column1
                : column2
            }
            defaultColDef={defaultColDef}
            suppressColumnVirtualisation={true}
            suppressRowVirtualisation={true}
            pagination={true}
            localeText={AG_GRID_LOCALE_VN}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            cacheBlockSize={paginationPageSize}
            maxBlocksInCache={3}
            // getContextMenuItems={getContextMenuItems}
            // statusBar={statusBar}
          />
        </div>
      </div>
    </Box>
  );
};
